import { z } from "zod";
import { LocalizedTextSchema, ManufacturerSchema, TierSchema, idPattern } from "./common.js";

export const ModuleSlotSchema = z.enum(["weapon", "armor", "core", "sensor", "drive"]);
export type ModuleSlot = z.infer<typeof ModuleSlotSchema>;
export const MODULE_SLOTS: readonly ModuleSlot[] = ["weapon", "armor", "core", "sensor", "drive"];

/** Stat keys a module can carry; they mirror the rules `StatBlock` keys. */
export const ModuleStatSchema = z.enum(["hp", "atk", "def", "crit", "critDmg", "acc", "eva", "energyPerAttack", "intervalTicks"]);
export type ModuleStat = z.infer<typeof ModuleStatSchema>;

/** Percent stats add `value %` to the stat; flat stats add `value` after the multipliers. */
export const MODULE_STAT_MODE: Record<ModuleStat, "pct" | "flat"> = {
  hp: "pct",
  atk: "pct",
  def: "pct",
  crit: "pct",
  critDmg: "pct",
  acc: "flat",
  eva: "flat",
  energyPerAttack: "pct",
  intervalTicks: "pct",
};

export const ModuleDefSchema = z.object({
  id: idPattern("mod"),
  slot: ModuleSlotSchema,
  tier: TierSchema,
  mfr: ManufacturerSchema,
  name: LocalizedTextSchema,
  /** Main stat and its tier-scaled base value (percent points or flat, see MODULE_STAT_MODE). */
  main: z.object({ stat: ModuleStatSchema, base: z.number().positive() }),
  /** Substats are rolled from this pool without replacement (D 0, C 1, B 1, A 2, S 3). */
  substatPool: z.array(ModuleStatSchema).min(2),
  /** Craftable in the Armory from a blueprint (tiers B/A/S); D and C modules only drop. */
  blueprint: z.boolean(),
  description: LocalizedTextSchema,
});
export type ModuleDef = z.infer<typeof ModuleDefSchema>;

export const ENHANCE_MAX = 15;
/** +5 % of the main stat per enhancement step (30-units.md R26). */
export const ENHANCE_STEP_PCT = 5;
